import { snapRoundDecimal } from '@calculations/round/decimal';
import { SnapUnsupportedCalculation } from '@snap-types/models/enums';
import { dec, Decimal, ZERO } from '@snap-types/types/decimal';

import {
  calculateOperationCosts,
  SnapOperationCalculationResults,
  SnapOperationCostParams,
} from './operation';

export type SnapWorkOrderCostParams = {
  quantity: Decimal;
  snapWorkOrder: {
    snapOperations: SnapOperationCostParams[];
    hasSubWorkOrders: boolean;
    hasNestedRequirementWorkOrders: boolean;
    enableUnsupportedCalculationsFlag: boolean;
  };
};

export type SnapWorkOrderCostResults = {
  totalCost: Decimal;
  totalMaterialCost: Decimal;
  totalLaborCost: Decimal;
  totalBurdenCost: Decimal;
  totalServiceCost: Decimal;

  unitCost: Decimal;
  unitMaterialCost: Decimal;
  unitLaborCost: Decimal;
  unitBurdenCost: Decimal;
  unitServiceCost: Decimal;
  unitVariableCosts: Decimal;

  requiresUnsupportedCalculations: SnapUnsupportedCalculation[];

  operationResults: SnapOperationCalculationResults[];
};

export function reverseSortOperations(
  operationParams: SnapOperationCostParams[],
) {
  return operationParams.toSorted((a, b) => b.sequenceNo - a.sequenceNo);
}

/**
 * Calculates Work Order costs based on the provided parameters.
 *
 * @remarks
 *
 * Assumes quantity and costs are in self-consistent units (e.g. quantity and
 * run rate agree). It is the responsibility of the caller to ensure that the
 * input parameters are in the correct units and to handle any necessary
 * conversions before calling this function, as well as to interpret the
 * output correctly.
 */
export function calculateWorkOrderCosts(
  params: SnapWorkOrderCostParams,
): SnapWorkOrderCostResults {
  // sort the operations by sequence number in reverse order
  //  to work out the operation start quantities with scrap
  const operations = reverseSortOperations(params.snapWorkOrder.snapOperations);

  // start with the final operation end quantity = the work order quantity
  let operationEndQuantity = params.quantity;

  if (operationEndQuantity.isZero()) {
    throw new Error('Cannot calculate work order costs with a zero quantity');
  }

  // Prevent calculation exceptions when there are no operations
  if (operations.length === 0) {
    return emptyWorkOrderCostResults();
  }

  const results: SnapOperationCalculationResults[] = [];

  for (const operation of operations) {
    // calculate operation values
    const opCalculationResult = calculateOperationCosts(
      operation,
      operationEndQuantity,
    );

    results.push(opCalculationResult);

    // set the end quantity (of the prior operation)
    //  to the start quantity (of the current operation)
    operationEndQuantity = opCalculationResult.startQuantity;
  }

  // re-sort by sequence number in order
  results.reverse();

  // compute total costs from all operations
  const totalMaterialCost = Decimal.sum(
    ...results.map((operation) => operation.materialCost),
  );
  const totalLaborCost = Decimal.sum(...results.map((op) => op.laborCost));
  const totalBurdenCost = Decimal.sum(...results.map((op) => op.burdenCost));
  const totalServiceCost = Decimal.sum(...results.map((op) => op.serviceCost));

  // to get unit costs, divide the cost by the WORK ORDER end quantity (from input params)
  const unitMaterialCost = Decimal.div(totalMaterialCost, params.quantity);
  const unitLaborCost = Decimal.div(totalLaborCost, params.quantity);
  const unitBurdenCost = Decimal.div(totalBurdenCost, params.quantity);
  const unitServiceCost = Decimal.div(totalServiceCost, params.quantity);

  const requiresUnsupportedCalculations: SnapUnsupportedCalculation[] = [];

  const {
    enableUnsupportedCalculationsFlag,
    hasNestedRequirementWorkOrders,
    hasSubWorkOrders,
  } = params.snapWorkOrder;

  if (enableUnsupportedCalculationsFlag) {
    requiresUnsupportedCalculations.push(
      ...results.flatMap((op) => op.requiresUnsupportedCalculations),
    );

    if (hasSubWorkOrders) {
      requiresUnsupportedCalculations.push(
        SnapUnsupportedCalculation.PART_HAS_SUB_OPERATIONS,
      );
    }

    if (hasNestedRequirementWorkOrders) {
      requiresUnsupportedCalculations.push(
        SnapUnsupportedCalculation.REQUIREMENT_HAS_SUBASSEMBLY,
      );
    }
  }

  // If costs are unreliable, hardcode the aggregates to $1 to not give the impression of accuracy
  const unitVariableCosts =
    requiresUnsupportedCalculations.length > 0
      ? dec(1)
      : Decimal.sum(unitLaborCost, unitMaterialCost, unitServiceCost);

  const totalCost =
    requiresUnsupportedCalculations.length > 0
      ? dec(1)
      : Decimal.sum(
          totalMaterialCost,
          totalLaborCost,
          totalBurdenCost,
          totalServiceCost,
        );

  const unitCost =
    requiresUnsupportedCalculations.length > 0
      ? dec(1)
      : Decimal.div(totalCost, params.quantity);

  return {
    totalCost: snapRoundDecimal(totalCost),
    totalMaterialCost: snapRoundDecimal(totalMaterialCost),
    totalBurdenCost: snapRoundDecimal(totalBurdenCost),
    totalLaborCost: snapRoundDecimal(totalLaborCost),
    totalServiceCost: snapRoundDecimal(totalServiceCost),

    unitCost: snapRoundDecimal(unitCost),
    unitMaterialCost: snapRoundDecimal(unitMaterialCost),
    unitLaborCost: snapRoundDecimal(unitLaborCost),
    unitBurdenCost: snapRoundDecimal(unitBurdenCost),
    unitServiceCost: snapRoundDecimal(unitServiceCost),
    unitVariableCosts: snapRoundDecimal(unitVariableCosts),

    requiresUnsupportedCalculations,

    operationResults: results,
  };
}

function emptyWorkOrderCostResults(): SnapWorkOrderCostResults {
  return {
    totalCost: ZERO,
    totalMaterialCost: ZERO,
    totalBurdenCost: ZERO,
    totalLaborCost: ZERO,
    totalServiceCost: ZERO,

    unitCost: ZERO,
    unitMaterialCost: ZERO,
    unitLaborCost: ZERO,
    unitBurdenCost: ZERO,
    unitServiceCost: ZERO,
    unitVariableCosts: ZERO,

    requiresUnsupportedCalculations: [],

    operationResults: [],
  };
}
